// ==============================================
// Subscription Page
// ==============================================

import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, ExclamationTriangleIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { DevicePhoneMobileIcon, ServerIcon, PlusIcon, ClipboardIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { copyToClipboard } from '../../lib/clipboard';
import { useToastStore } from '../../store/toastStore';
import { AddDeviceModal } from '../../components/devices/AddDeviceModal';
import type { Package } from '../../types/subscription';
import type { Device } from '../../types/device';
import clsx from 'clsx';
import { planKeyFeatures } from '../../utils/planKeyFeatures';

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 0,
  });
}

function formatTokens(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(0)}K`;
  }
  return tokens.toString();
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function UsageBar({ used, total }: { used: number; total: number }) {
  const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;

  return (
    <div className="space-y-2">
      <div className="relative h-6 bg-slate-700 rounded-full overflow-hidden">
        {/* Used portion */}
        <div
          className={clsx(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-500',
            percentage > 90
              ? 'bg-gradient-to-r from-amber-500 to-red-500'
              : 'bg-gradient-to-r from-cyan-500 to-blue-600'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-slate-400">
          <span className="text-white font-medium">{formatTokens(used)}</span> used
        </span>
        <span className="text-slate-400">
          <span className="text-white font-medium">{formatTokens(Math.max(total - used, 0))}</span> remaining
        </span>
      </div>
    </div>
  );
}

// Upgrade Subscription Modal Component
function UpgradeSubscriptionModal({
  isOpen,
  onClose,
  onConfirmUpgrade,
  currentPackage,
  newPackage,
  daysRemaining,
  isProcessing,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirmUpgrade: () => void;
  currentPackage: Package;
  newPackage: Package;
  daysRemaining: number;
  isProcessing: boolean;
}) {
  if (!isOpen) return null;

  // Calculate prorated credit (quarterly billing — 91 days per quarter)
  const daysInPeriod = 91;
  const quarterlyPrice = currentPackage.monthly_price_cents * 3;
  const dailyRate = quarterlyPrice / daysInPeriod;
  // Exclude the upgrade day from credit calculation (upgrade day belongs to new plan)
  const creditableDays = Math.max(0, daysRemaining - 1);
  // Cap days at the period length to prevent credit exceeding subscription value
  const cappedCreditableDays = Math.min(creditableDays, daysInPeriod - 1);
  // Calculate credit and cap it at the quarterly subscription price
  const proratedCredit = Math.min(
    Math.round(dailyRate * cappedCreditableDays),
    quarterlyPrice
  );
  const newPlanCost = newPackage.monthly_price_cents * 3;
  const amountDueToday = Math.max(0, newPlanCost - proratedCredit);
  const today = new Date().toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-800 rounded-2xl border border-slate-700 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-cyan-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Upgrade Your Plan</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Plan Change Summary */}
          <div className="flex items-center gap-4">
            {/* Current Plan */}
            <div className="flex-1 p-4 bg-slate-900/50 rounded-xl border border-slate-600 text-center">
              <p className="text-sm text-slate-400 mb-1">Current Plan</p>
              <p className="text-lg font-semibold text-white">{currentPackage.name}</p>
              <p className="text-sm text-slate-400">{formatPrice(currentPackage.monthly_price_cents * 3)}/quarter</p>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>

            {/* New Plan */}
            <div className="flex-1 p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/30 text-center">
              <p className="text-sm text-cyan-400 mb-1">New Plan</p>
              <p className="text-lg font-semibold text-white">{newPackage.name}</p>
              <p className="text-sm text-cyan-400">{formatPrice(newPackage.monthly_price_cents * 3)}/quarter</p>
            </div>
          </div>

          {/* Prorated Credit Calculation */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-600 space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Prorated Credit Calculation
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>Days credited (excludes today)</span>
                <span className="text-white">{cappedCreditableDays} days</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Current plan daily rate</span>
                <span className="text-white">{formatPrice(Math.round(dailyRate))}/day</span>
              </div>
              <div className="flex justify-between text-slate-400 border-t border-slate-700 pt-2">
                <span>Prorated credit</span>
                <span className="text-green-400 font-medium">-{formatPrice(proratedCredit)}</span>
              </div>
            </div>
          </div>

          {/* Amount Summary */}
          <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/30 space-y-3">
            <div className="flex justify-between text-slate-300">
              <span>New plan ({newPackage.name})</span>
              <span>{formatPrice(newPlanCost)}</span>
            </div>
            <div className="flex justify-between text-green-400">
              <span>Prorated credit applied</span>
              <span>-{formatPrice(proratedCredit)}</span>
            </div>
            <div className="flex justify-between text-white font-semibold border-t border-cyan-500/30 pt-3">
              <span>Amount due today</span>
              <span className="text-cyan-400">{formatPrice(amountDueToday)}</span>
            </div>
          </div>

          {/* Important Info */}
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Upgrade takes effect immediately</p>
                <p className="text-slate-400">
                  Your new plan starts today ({today}) with full access to {newPackage.name} features.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">New billing cycle begins</p>
                <p className="text-slate-400">
                  Your new billing period starts today and renews quarterly.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Increased token allowance</p>
                <p className="text-slate-400">
                  You'll receive {formatTokens(newPackage.included_tokens * 3)} tokens/quarter instead of {formatTokens(currentPackage.included_tokens * 3)}.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-700 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirmUpgrade}
            disabled={isProcessing}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-medium hover:from-cyan-400 hover:to-blue-500 transition-colors disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : `Upgrade for ${formatPrice(amountDueToday)}`}
          </button>
        </div>
      </div>
    </div>
  );
}


// Downgrade Subscription Modal Component
function DowngradeSubscriptionModal({
  isOpen,
  onClose,
  onConfirmDowngrade,
  currentPackage,
  newPackage,
  periodEndDate,
  isProcessing,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirmDowngrade: () => void;
  currentPackage: Package;
  newPackage: Package;
  periodEndDate: string;
  isProcessing: boolean;
}) {
  if (!isOpen) return null;

  const endDate = new Date(periodEndDate);
  const nextBillingDate = new Date(endDate);
  nextBillingDate.setDate(nextBillingDate.getDate() + 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-800 rounded-2xl border border-slate-700 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-white">Downgrade Your Plan</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Plan Change Summary */}
          <div className="flex items-center gap-4">
            {/* Current Plan */}
            <div className="flex-1 p-4 bg-slate-900/50 rounded-xl border border-slate-600 text-center">
              <p className="text-sm text-slate-400 mb-1">Current Plan</p>
              <p className="text-lg font-semibold text-white">{currentPackage.name}</p>
              <p className="text-sm text-slate-400">{formatPrice(currentPackage.monthly_price_cents * 3)}/quarter</p>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>

            {/* New Plan */}
            <div className="flex-1 p-4 bg-amber-500/10 rounded-xl border border-amber-500/30 text-center">
              <p className="text-sm text-amber-400 mb-1">New Plan</p>
              <p className="text-lg font-semibold text-white">{newPackage.name}</p>
              <p className="text-sm text-amber-400">{formatPrice(newPackage.monthly_price_cents * 3)}/quarter</p>
            </div>
          </div>

          {/* Important Timing Info */}
          <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
            <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2 mb-3">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Important: Timing of Your Downgrade
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-400 font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="text-white font-medium">Keep your current benefits until {formatDate(periodEndDate)}</p>
                  <p className="text-slate-400">
                    You've already paid for {currentPackage.name}. Continue enjoying {formatTokens(currentPackage.included_tokens * 3)} tokens/quarter until your current billing period ends.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-amber-400 font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="text-white font-medium">{newPackage.name} begins on {formatDate(periodEndDate)}</p>
                  <p className="text-slate-400">
                    Your new plan with {formatTokens(newPackage.included_tokens * 3)} tokens/quarter starts at the end of your current period.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What Changes */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-600 space-y-3">
            <h3 className="text-sm font-semibold text-white">What changes with {newPackage.name}:</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Quarterly token allowance</span>
                <div className="text-right">
                  <span className="text-slate-500 line-through mr-2">{formatTokens(currentPackage.included_tokens * 3)}</span>
                  <span className="text-white">{formatTokens(newPackage.included_tokens * 3)}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Quarterly price</span>
                <div className="text-right">
                  <span className="text-slate-500 line-through mr-2">{formatPrice(currentPackage.monthly_price_cents * 3)}</span>
                  <span className="text-green-400">{formatPrice(newPackage.monthly_price_cents * 3)}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Quarterly savings</span>
                <span className="text-green-400 font-medium">
                  {formatPrice((currentPackage.monthly_price_cents - newPackage.monthly_price_cents) * 3)}/quarter
                </span>
              </div>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Keep access to previously downloaded imagery</p>
                <p className="text-slate-400">
                  Unlike cancellation, downgrading preserves access to all satellite imagery you've acquired.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">No charge today</p>
                <p className="text-slate-400">
                  You won't be charged until your new plan begins on {formatDate(periodEndDate)}.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-700 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
          >
            Keep Current Plan
          </button>
          <button
            onClick={onConfirmDowngrade}
            disabled={isProcessing}
            className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Confirm Downgrade'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Cancel Subscription Modal Component
function CancelSubscriptionModal({
  isOpen,
  onClose,
  onConfirmCancel,
  onDowngrade,
  currentPackage,
  downgradePlans,
  periodEndDate,
  isCanceling,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirmCancel: () => void;
  onDowngrade: (packageSlug: string) => void;
  currentPackage: Package;
  downgradePlans: Package[];
  periodEndDate: string;
  isCanceling: boolean;
}) {
  const [showDowngradeOptions, setShowDowngradeOptions] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-slate-800 rounded-2xl border border-slate-700 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-white">Cancel Subscription?</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Plan Info */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-600">
            <p className="text-sm text-slate-400 mb-1">Current Plan</p>
            <p className="text-lg font-semibold text-white">{currentPackage.name}</p>
            <p className="text-sm text-slate-400">
              Your subscription will remain active until <span className="text-white font-medium">{formatDate(periodEndDate)}</span>
            </p>
          </div>

          {/* Warning Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">What you'll lose after {formatDate(periodEndDate)}:</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <XMarkIcon className="w-3 h-3 text-red-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Quarterly token allowance</p>
                  <p className="text-sm text-slate-400">
                    Your {formatTokens(currentPackage.included_tokens * 3)} tokens/quarter will no longer be available
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <XMarkIcon className="w-3 h-3 text-red-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Access to downloaded imagery</p>
                  <p className="text-sm text-slate-400">
                    All satellite imagery acquired as part of your {currentPackage.name} plan will become inaccessible
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <XMarkIcon className="w-3 h-3 text-red-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Plan benefits & features</p>
                  <p className="text-sm text-slate-400">
                    Priority generation, extended download links, and other {currentPackage.name} features
                  </p>
                </div>
              </li>
            </ul>
          </div>

          {/* Downgrade Suggestion */}
          {downgradePlans.length > 0 ? (
            <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckIcon className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">Consider downgrading instead</h4>
                  <p className="text-sm text-slate-300 mb-3">
                    By downgrading to a lower plan, you'll have reduced quarterly benefits but
                    <span className="text-cyan-400 font-medium"> retain access to all previously downloaded imagery</span>.
                    The change will take effect at the end of your current billing period.
                  </p>
                  {!showDowngradeOptions ? (
                    <button
                      onClick={() => setShowDowngradeOptions(true)}
                      className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      View downgrade options →
                    </button>
                  ) : (
                    <div className="space-y-2 mt-3">
                      {downgradePlans.map((pkg) => (
                        <button
                          key={pkg.id}
                          onClick={() => onDowngrade(pkg.slug)}
                          className="w-full flex items-center justify-between p-3 bg-slate-800 rounded-lg border border-slate-600 hover:border-cyan-500/50 transition-colors"
                        >
                          <div className="text-left">
                            <p className="text-white font-medium">{pkg.name}</p>
                            <p className="text-sm text-slate-400">
                              {formatTokens(pkg.included_tokens * 3)} tokens/quarter
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-cyan-400 font-semibold">
                              {formatPrice(pkg.monthly_price_cents * 3)}/quarter
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-700/50 border border-slate-600 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                  <ExclamationTriangleIcon className="w-4 h-4 text-slate-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">You're on our most affordable plan</h4>
                  <p className="text-sm text-slate-400">
                    There are no lower-priced plans available. If you cancel, you will lose access to all
                    benefits and previously downloaded imagery after your current billing period ends.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-slate-700 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
          >
            Keep My Subscription
          </button>
          <button
            onClick={onConfirmCancel}
            disabled={isCanceling}
            className="flex-1 px-4 py-3 bg-red-500/20 text-red-400 rounded-xl font-medium hover:bg-red-500/30 transition-colors border border-red-500/30 disabled:opacity-50"
          >
            {isCanceling ? 'Canceling...' : 'Cancel Anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SubscriptionPage() {
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedUpgradePackage, setSelectedUpgradePackage] = useState<Package | null>(null);
  const [selectedDowngradePackage, setSelectedDowngradePackage] = useState<Package | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isSuccess = searchParams.get('success') === 'true';
  const [showSuccessMessage, setShowSuccessMessage] = useState(isSuccess);
  const [waitingForWebhook, setWaitingForWebhook] = useState(false);
  const pollCountRef = useRef(0);

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.getMyUsage(),
    refetchInterval: waitingForWebhook ? 2000 : false,
  });

  const { data: packages, isLoading: packagesLoading } = useQuery({
    queryKey: ['packages'],
    queryFn: () => api.getPackages(),
  });

  // Device queries for registration codes
  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['devices', 'my'],
    queryFn: () => api.getMyDevices(),
  });

  const devices = devicesData?.devices || [];

  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopyRegistrationCode = async (code: string) => {
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Copied!',
        message: 'Registration code copied.',
      });
    } else {
      useToastStore.getState().addToast({
        type: 'error',
        title: 'Copy failed',
        message: 'Long-press the registration code to copy it manually.',
      });
    }
  };

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelSubscription(),
    onSuccess: () => {
      setShowCancelModal(false);
      queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.reactivateSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
  });

  const renewMutation = useMutation({
    mutationFn: () => api.renewSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
  });

  // Handle downgrade from cancel modal
  const handleDowngrade = (packageSlug: string) => {
    setShowCancelModal(false);
    // Find the package and show the downgrade modal
    const pkg = packages?.find(p => p.slug === packageSlug);
    if (pkg) {
      setSelectedDowngradePackage(pkg);
      setShowDowngradeModal(true);
    }
  };

  // Get downgrade plan options (plans cheaper than current)
  const getDowngradePlans = (): Package[] => {
    if (!packages || !usage?.package) return [];
    return packages.filter(
      (pkg) =>
        pkg.id !== usage.package?.id &&
        pkg.monthly_price_cents < (usage.package?.monthly_price_cents ?? 0) &&
        pkg.monthly_price_cents > 0 &&
        pkg.visibility === 'active'
    );
  };

  // Poll for subscription after hosted checkout (webhook may still be processing)
  useEffect(() => {
    if (!isSuccess) return;
    const sub = usage?.subscription;
    if (sub && sub.status === 'active') {
      // Subscription arrived — stop polling, show success
      if (waitingForWebhook) {
        setWaitingForWebhook(false);
        pollCountRef.current = 0;
        setShowSuccessMessage(true);
      }
      return;
    }
    // No active subscription yet after success redirect — start polling
    if (!usageLoading && !sub) {
      if (!waitingForWebhook) setWaitingForWebhook(true);
      pollCountRef.current += 1;
      // Give up after ~20 seconds (10 polls at 2s)
      if (pollCountRef.current > 10) {
        setWaitingForWebhook(false);
        setShowSuccessMessage(true); // show anyway — subscription will appear on refresh
      }
    }
  }, [isSuccess, usage?.subscription, usageLoading, waitingForWebhook]);

  // Clear success message after showing it
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        // Clear the success param from URL
        if (searchParams.get('success')) {
          searchParams.delete('success');
          setSearchParams(searchParams);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, searchParams, setSearchParams]);

  const subscription = usage?.subscription;
  const currentPackage = usage?.package;

  if (usageLoading || waitingForWebhook) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        {waitingForWebhook && (
          <p className="text-slate-400 text-sm">Setting up your subscription...</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Subscription</h1>
        <p className="text-slate-400">Manage your subscription and view usage</p>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="p-4 bg-green-500/10 border border-green-500/50 rounded-xl flex items-center gap-3">
          <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" />
          <div>
            <p className="text-green-400 font-medium">Subscription activated!</p>
            <p className="text-sm text-green-400/80">
              Your subscription has been set up successfully. You now have access to your plan features.
            </p>
          </div>
        </div>
      )}

      {subscription && currentPackage ? (
        <>
          {/* Device Registration Section */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            {(() => {
              const maxDevices = (currentPackage as any)?.max_devices ?? 0;
              const activeDeviceCount = devices.filter((d: Device) => d.status !== 'revoked').length;
              const atLimit = maxDevices > 0 && activeDeviceCount >= maxDevices;

              return (
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Device Registration</h3>
                <p className="text-sm text-slate-400">
                  {maxDevices > 0
                    ? `${activeDeviceCount} of ${maxDevices} device${maxDevices === 1 ? '' : 's'} registered`
                    : 'Generate codes to register your VortexNav devices'}
                </p>
              </div>
              {atLimit ? (
                <span className="text-sm text-amber-400 font-medium">
                  Device limit reached
                </span>
              ) : (
              <button
                onClick={() => setShowAddDeviceModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
                New Device
              </button>
              )}
            </div>
              );
            })()}

            {/* Registered Devices List */}
            {devicesLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : devices.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <DevicePhoneMobileIcon className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="mb-1">No devices registered yet</p>
                <p className="text-sm text-slate-500">
                  Click "New Device" to generate a registration code
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {devices.map((device: Device) => (
                  <div
                    key={device.id}
                    className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      <div className={clsx(
                        'w-10 h-10 rounded-lg flex items-center justify-center',
                        device.device_type === 'scout' ? 'bg-cyan-500/20' : 'bg-purple-500/20'
                      )}>
                        {device.device_type === 'scout' ? (
                          <DevicePhoneMobileIcon className="w-5 h-5 text-cyan-400" />
                        ) : (
                          <ServerIcon className="w-5 h-5 text-purple-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {device.name || device.serial_number || 'Unnamed Device'}
                        </p>
                        <p className="text-sm text-slate-400">
                          {device.device_type === 'scout' ? 'Scout' : 'Command Hub'}
                          {device.serial_number && ` • ${device.serial_number}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Show registration code if pending */}
                      {device.status === 'pending' && device.registration_code && (
                        <div className="flex items-center gap-2">
                          <code className="px-2 py-1 bg-slate-800 text-sm font-mono text-cyan-400 rounded">
                            {device.registration_code}
                          </code>
                          <button
                            onClick={() => handleCopyRegistrationCode(device.registration_code!)}
                            className="p-1 text-slate-400 hover:text-white transition-colors"
                            title="Copy code"
                          >
                            {copiedCode === device.registration_code ? (
                              <ClipboardDocumentCheckIcon className="w-4 h-4 text-green-400" />
                            ) : (
                              <ClipboardIcon className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}
                      <span className={clsx(
                        'px-2 py-1 text-sm rounded-full',
                        device.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        device.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        device.status === 'suspended' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-red-500/20 text-red-400'
                      )}>
                        {device.status}
                      </span>
                    </div>
                  </div>
                ))}
                <Link
                  to="/dashboard/devices"
                  className="block text-center text-sm text-cyan-400 hover:text-cyan-300 py-2"
                >
                  Manage all devices →
                </Link>
              </div>
            )}
          </div>

          {/* Current Plan Card */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-xl font-bold text-white">{currentPackage.name}</h2>
                  <span
                    className={clsx(
                      'px-2 py-1 rounded-full text-sm font-medium',
                      subscription.status === 'active'
                        ? 'bg-green-500/10 text-green-400'
                        : subscription.status === 'canceled'
                        ? 'bg-red-500/10 text-red-400'
                        : 'bg-amber-500/10 text-amber-400'
                    )}
                  >
                    {subscription.status}
                  </span>
                </div>
                <p className="text-slate-400">
                  {formatPrice(currentPackage.monthly_price_cents * 3)}/quarter
                </p>
              </div>
              <button
                onClick={() => setShowChangePlan(!showChangePlan)}
                className="px-4 py-2 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Change Plan
              </button>
            </div>

            {/* Usage */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-slate-400 mb-3">Quarterly Usage</h3>
              <UsageBar
                used={usage?.tokens_used ?? 0}
                // Already the quarter's allowance (/api/users/me/usage), as on Billing.
                total={usage?.tokens_included ?? 0}
              />
            </div>

            {/* Billing Period */}
            <div className="grid sm:grid-cols-2 gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div>
                <p className="text-sm text-slate-400">Current Period</p>
                <p className="text-white">
                  {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Days Remaining</p>
                  <p className="text-white">{usage?.days_remaining ?? 0} days</p>
                </div>
                <button
                  onClick={() => renewMutation.mutate()}
                  disabled={renewMutation.isPending}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-500 transition-colors disabled:opacity-50 text-sm"
                >
                  {renewMutation.isPending ? 'Renewing...' : 'Renew'}
                </button>
              </div>
            </div>

            {/* Cancel Warning with Reactivate Button */}
            {subscription.cancel_at_period_end && (
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-amber-400 font-medium">Subscription ending</p>
                    <p className="text-sm text-amber-400/80 mb-3">
                      Your subscription will end on {formatDate(subscription.current_period_end)}.
                      After this date, you will lose access to your plan benefits and any satellite imagery acquired through this subscription.
                    </p>
                    <button
                      onClick={() => reactivateMutation.mutate()}
                      disabled={reactivateMutation.isPending}
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-400 transition-colors disabled:opacity-50"
                    >
                      {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate Subscription'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Plan Features */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Plan Features</h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              <li className="flex items-center gap-2 text-slate-300">
                <CheckIcon className="w-5 h-5 text-cyan-400" />
                {formatTokens(currentPackage.included_tokens * 3)} tokens/quarter
              </li>
              {currentPackage.cost_per_1000_tokens_cents > 0 && (
              <li className="flex items-center gap-2 text-slate-300">
                <CheckIcon className="w-5 h-5 text-cyan-400" />
                {formatPrice(currentPackage.cost_per_1000_tokens_cents)}/1K overage
              </li>
              )}
              {currentPackage.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-slate-300">
                  <CheckIcon className="w-5 h-5 text-cyan-400" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Add Device Modal */}
          {showAddDeviceModal && (
            <AddDeviceModal onClose={() => setShowAddDeviceModal(false)} />
          )}

          {/* Cancel Subscription Modal */}
          {currentPackage && (
            <CancelSubscriptionModal
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
              onConfirmCancel={() => cancelMutation.mutate()}
              onDowngrade={handleDowngrade}
              currentPackage={currentPackage}
              downgradePlans={getDowngradePlans()}
              periodEndDate={subscription.current_period_end}
              isCanceling={cancelMutation.isPending}
            />
          )}

          {/* Upgrade Subscription Modal */}
          {currentPackage && selectedUpgradePackage && (
            <UpgradeSubscriptionModal
              isOpen={showUpgradeModal}
              onClose={() => {
                setShowUpgradeModal(false);
                setSelectedUpgradePackage(null);
              }}
              onConfirmUpgrade={() => {
                setShowUpgradeModal(false);
                navigate(`/dashboard/checkout?package=${selectedUpgradePackage.slug}`);
              }}
              currentPackage={currentPackage}
              newPackage={selectedUpgradePackage}
              daysRemaining={usage?.days_remaining ?? 0}
              isProcessing={false}
            />
          )}

          {/* Downgrade Subscription Modal */}
          {currentPackage && selectedDowngradePackage && (
            <DowngradeSubscriptionModal
              isOpen={showDowngradeModal}
              onClose={() => {
                setShowDowngradeModal(false);
                setSelectedDowngradePackage(null);
              }}
              onConfirmDowngrade={() => {
                setShowDowngradeModal(false);
                navigate(`/dashboard/checkout?package=${selectedDowngradePackage.slug}`);
              }}
              currentPackage={currentPackage}
              newPackage={selectedDowngradePackage}
              periodEndDate={subscription.current_period_end}
              isProcessing={false}
            />
          )}

          {/* Change Plan Modal */}
          {showChangePlan && packages && currentPackage && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowChangePlan(false)} />
              <div className="relative bg-slate-800 rounded-2xl border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-700">
                  <div>
                    <h2 className="text-xl font-bold text-white">Change Your Plan</h2>
                    <p className="text-sm text-slate-400 mt-1">
                      Currently on <span className="text-cyan-400 font-medium">{currentPackage.name}</span> — {formatPrice(currentPackage.monthly_price_cents * 3)}/quarter
                    </p>
                  </div>
                  <button
                    onClick={() => setShowChangePlan(false)}
                    className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-700"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Package Grid */}
                <div className="p-6">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {packages
                      .filter((pkg) => pkg.visibility === 'active' && pkg.monthly_price_cents > 0)
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((pkg) => {
                        const isCurrent = pkg.id === currentPackage.id;
                        const isUpgrade = !isCurrent && pkg.monthly_price_cents > currentPackage.monthly_price_cents;
                        const quarterlyTokens = pkg.included_tokens * 3;

                        const keyFeatures = planKeyFeatures(pkg);

                        return (
                          <div
                            key={pkg.id}
                            className={clsx(
                              'relative flex flex-col p-5 rounded-xl border-2 transition-all',
                              isCurrent
                                ? 'border-green-500/50 bg-green-500/5'
                                : isUpgrade
                                ? 'border-cyan-500/30 bg-cyan-500/5 hover:border-cyan-500/60'
                                : 'border-slate-600 hover:border-slate-500'
                            )}
                          >
                            {/* Badges */}
                            <div className="flex items-center gap-2 mb-3">
                              {isCurrent && (
                                <span className="px-2 py-0.5 text-sm font-semibold bg-green-500/20 text-green-400 rounded-full">
                                  Current Plan
                                </span>
                              )}
                              {pkg.is_popular && !isCurrent && (
                                <span className="px-2 py-0.5 text-sm font-semibold bg-cyan-500/20 text-cyan-400 rounded-full">
                                  Popular
                                </span>
                              )}
                              {isUpgrade && (
                                <span className="px-2 py-0.5 text-sm font-semibold bg-cyan-500/20 text-cyan-400 rounded-full">
                                  Upgrade
                                </span>
                              )}
                            </div>

                            {/* Name & Price */}
                            <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
                            {pkg.description && (
                              <p className="text-sm text-slate-500 mt-0.5">{pkg.description}</p>
                            )}
                            <div className="mt-3 mb-4">
                              <span className="text-2xl font-bold text-white">{formatPrice(pkg.monthly_price_cents * 3)}</span>
                              <span className="text-sm text-slate-400">/quarter</span>
                            </div>

                            {/* Tokens */}
                            <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-slate-900/50 rounded-lg">
                              <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              <span className="text-sm text-white font-medium">{formatTokens(quarterlyTokens)}</span>
                              <span className="text-sm text-slate-400">tokens/quarter</span>
                            </div>

                            {/* Features */}
                            <div className="flex-1 space-y-1.5 mb-5">
                              {keyFeatures.slice(0, 6).map((feat) => (
                                <div key={feat} className="flex items-center gap-2 text-sm">
                                  <CheckIcon className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                                  <span className="text-slate-300">{feat}</span>
                                </div>
                              ))}
                              {keyFeatures.length > 6 && (
                                <p className="text-sm text-slate-500 pl-5">+{keyFeatures.length - 6} more</p>
                              )}
                              {keyFeatures.length === 0 && (
                                <p className="text-sm text-slate-500">Basic features</p>
                              )}
                              {/* Custom features from array */}
                              {(pkg.features || []).slice(0, 3).map((feat) => (
                                <div key={feat} className="flex items-center gap-2 text-sm">
                                  <CheckIcon className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                                  <span className="text-slate-300">{feat}</span>
                                </div>
                              ))}
                            </div>

                            {/* Action Button */}
                            {isCurrent ? (
                              <div className="w-full px-4 py-2.5 text-center text-sm font-medium text-green-400 border border-green-500/30 rounded-xl bg-green-500/5">
                                Your Current Plan
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setShowChangePlan(false);
                                  if (isUpgrade) {
                                    setSelectedUpgradePackage(pkg);
                                    setShowUpgradeModal(true);
                                  } else {
                                    setSelectedDowngradePackage(pkg);
                                    setShowDowngradeModal(true);
                                  }
                                }}
                                className={clsx(
                                  'w-full px-4 py-2.5 text-sm font-medium rounded-xl transition-colors',
                                  isUpgrade
                                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500'
                                    : 'bg-slate-700 text-white hover:bg-slate-600'
                                )}
                              >
                                {isUpgrade ? 'Upgrade' : 'Downgrade'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>

                  {/* Footer info */}
                  <div className="mt-6 flex flex-col sm:flex-row gap-4 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4 text-cyan-400" />
                      <span>Upgrades take effect immediately with prorated billing</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4 text-amber-400" />
                      <span>Downgrades take effect at end of current billing period</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* No Subscription - Show Available Packages */
        <div className="space-y-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Choose Your Plan</h2>
            <p className="text-slate-400">
              Subscribe to a plan to get quarterly token allowances and priority features.
            </p>
          </div>

          {/* Available Packages */}
          {packagesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : packages && packages.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages
                .filter((pkg) => pkg.visibility === 'active' && pkg.monthly_price_cents > 0)
                .map((pkg) => (
                  <div
                    key={pkg.id}
                    className={clsx(
                      'relative p-6 rounded-xl border transition-all',
                      pkg.is_popular
                        ? 'bg-gradient-to-b from-cyan-500/10 to-slate-800/50 border-cyan-500/50'
                        : 'bg-slate-800/50 border-slate-700 hover:border-cyan-500/50'
                    )}
                  >
                    {pkg.is_popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-cyan-500 text-white text-sm font-semibold rounded-full">
                        Most Popular
                      </span>
                    )}
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
                      <p className="text-sm text-slate-400">{pkg.description}</p>
                    </div>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-white">
                        {formatPrice(pkg.monthly_price_cents * 3)}
                      </span>
                      <span className="text-slate-400">/quarter</span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckIcon className="w-4 h-4 text-cyan-400" />
                        {formatTokens(pkg.included_tokens * 3)} tokens/quarter
                      </li>
                      {pkg.cost_per_1000_tokens_cents > 0 && (
                      <li className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckIcon className="w-4 h-4 text-cyan-400" />
                        {formatPrice(pkg.cost_per_1000_tokens_cents)}/1K overage
                      </li>
                      )}
                      {pkg.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                          <CheckIcon className="w-4 h-4 text-cyan-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate(`/dashboard/checkout?package=${pkg.slug}`)}
                      className={clsx(
                        'w-full py-3 px-4 rounded-xl font-semibold transition-all',
                        pkg.is_popular
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500'
                          : 'bg-slate-700 text-white hover:bg-slate-600'
                      )}
                    >
                      Subscribe to {pkg.name}
                    </button>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center text-slate-400 py-8">
              No plans available. Please check back later.
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default SubscriptionPage;
